import type { Geometry, LonLat, MapFeature, MapLayer } from "../domain/geo";

/*
 * A synthetic Madinah (OEMA) drawing, in the airports pack's layer
 * vocabulary, so the map has something real-shaped to draw without the
 * platform. The two runways use the published lengths (17/35 4,335 m,
 * 18/36 3,050 m); everything else is plausible, not surveyed. Labelled as
 * a sample like every other mock row.
 *
 * Geometry is authored in metres about the site centre and converted to
 * WGS-84 here, the inverse of the projection the map applies.
 */

const CENTRE: LonLat = [39.705, 24.553];
const KX = 111_320 * Math.cos((CENTRE[1] * Math.PI) / 180);
const KY = 111_320;

/** metres east/north of centre → lon/lat */
function ll(e: number, n: number): LonLat {
  return [+(CENTRE[0] + e / KX).toFixed(7), +(CENTRE[1] + n / KY).toFixed(7)];
}

/** A rotated rectangle (runway, taxiway, apron) as a closed ring. */
function rect(cx: number, cy: number, length: number, width: number, headingDeg: number): LonLat[] {
  const a = ((90 - headingDeg) * Math.PI) / 180; // heading → maths angle
  const ux = Math.cos(a),
    uy = Math.sin(a); // along
  const vx = -uy,
    vy = ux; // across
  const L = length / 2,
    W = width / 2;
  const corners: [number, number][] = [
    [cx + ux * L + vx * W, cy + uy * L + vy * W],
    [cx + ux * L - vx * W, cy + uy * L - vy * W],
    [cx - ux * L - vx * W, cy - uy * L - vy * W],
    [cx - ux * L + vx * W, cy - uy * L + vy * W],
  ];
  const ring = corners.map(([e, n]) => ll(e, n));
  ring.push(ring[0]);
  return ring;
}

function along(cx: number, cy: number, length: number, headingDeg: number, t: number): [number, number] {
  const a = ((90 - headingDeg) * Math.PI) / 180;
  return [cx + Math.cos(a) * length * (t - 0.5), cy + Math.sin(a) * length * (t - 0.5)];
}

const RWY1 = { cx: -400, cy: 0, len: 4335, wid: 60, hdg: 170 }; // 17/35
const RWY2 = { cx: 900, cy: 300, len: 3050, wid: 45, hdg: 178 }; // 18/36

let seq = 0;
function feat(layer: string, geometry: Geometry, props: Record<string, unknown>, kind?: string): MapFeature {
  seq += 1;
  return { id: `geo-${String(seq).padStart(4, "0")}`, layer, kind, geometry, props };
}

function poly(ring: LonLat[]): Geometry {
  return { type: "Polygon", coordinates: [ring] };
}
function line(pts: [number, number][]): Geometry {
  return { type: "LineString", coordinates: pts.map(([e, n]) => ll(e, n)) };
}
function pt(e: number, n: number): Geometry {
  return { type: "Point", coordinates: ll(e, n) };
}

/* pavements */
const pavement: MapFeature[] = [
  feat(
    "pavement",
    poly(rect(RWY1.cx, RWY1.cy, RWY1.len, RWY1.wid, RWY1.hdg)),
    { tag: "RWY-17-35", name: "Runway 17/35", surface_type: "asphalt", condition_score: 82 },
    "runway",
  ),
  feat(
    "pavement",
    poly(rect(RWY2.cx, RWY2.cy, RWY2.len, RWY2.wid, RWY2.hdg)),
    { tag: "RWY-18-36", name: "Runway 18/36", surface_type: "asphalt", condition_score: 88 },
    "runway",
  ),
  // parallel taxiway A east of 17/35, B connectors
  feat(
    "pavement",
    poly(rect(-160, 0, 4100, 25, 170)),
    { tag: "TWY-A", name: "Taxiway A", surface_type: "asphalt", condition_score: 79 },
    "taxiway",
  ),
  feat(
    "pavement",
    poly(rect(230, 1500, 900, 23, 80)),
    { tag: "TWY-B", name: "Taxiway B", surface_type: "asphalt", condition_score: 75 },
    "taxiway",
  ),
  feat(
    "pavement",
    poly(rect(230, 0, 900, 23, 80)),
    { tag: "TWY-C", name: "Taxiway C", surface_type: "asphalt", condition_score: 81 },
    "taxiway",
  ),
  feat(
    "pavement",
    poly(rect(230, -1500, 900, 23, 80)),
    { tag: "TWY-D", name: "Taxiway D", surface_type: "asphalt", condition_score: 84 },
    "taxiway",
  ),
  feat(
    "pavement",
    poly(rect(560, 1500, 900, 23, 170)),
    { tag: "TWY-E", name: "Taxiway E", surface_type: "asphalt", condition_score: 77 },
    "taxiway",
  ),
  // aprons
  feat(
    "pavement",
    poly(rect(450, 700, 700, 380, 80)),
    { tag: "APRON-1", name: "Apron 1 (contact stands)", surface_type: "concrete", condition_score: 71 },
    "apron",
  ),
  feat(
    "pavement",
    poly(rect(450, -700, 700, 380, 80)),
    { tag: "APRON-2", name: "Apron 2", surface_type: "concrete", condition_score: 74 },
    "apron",
  ),
  feat(
    "pavement",
    poly(rect(1500, -1200, 500, 300, 80)),
    { tag: "APRON-R", name: "Remote apron", surface_type: "concrete", condition_score: 62 },
    "apron",
  ),
  feat(
    "pavement",
    poly(rect(1700, 1400, 60, 60, 0)),
    { tag: "HELIPAD", name: "Helipad H", surface_type: "concrete", condition_score: 80 },
    "helipad",
  ),
];

/* markings: centrelines, thresholds, TDZ, holding positions */
const cl = (r: typeof RWY1, tag: string) => {
  const a = along(r.cx, r.cy, r.len - 120, r.hdg, 0);
  const b = along(r.cx, r.cy, r.len - 120, r.hdg, 1);
  return feat("markings", line([a, b]), { tag, marking_type: "centreline", colour: "white" });
};
const markings: MapFeature[] = [
  cl(RWY1, "MRK-CL-17-35"),
  cl(RWY2, "MRK-CL-18-36"),
  ...[0.06, 0.94].map((t, i) => {
    const [e, n] = along(RWY1.cx, RWY1.cy, RWY1.len, RWY1.hdg, t);
    return feat("markings", poly(rect(e, n, 45, 40, RWY1.hdg)), {
      tag: `MRK-THR-${i ? "35" : "17"}`,
      marking_type: "threshold",
      colour: "white",
    });
  }),
  ...[0.1, 0.9].map((t, i) => {
    const [e, n] = along(RWY1.cx, RWY1.cy, RWY1.len, RWY1.hdg, t);
    return feat("markings", poly(rect(e, n, 300, 30, RWY1.hdg)), {
      tag: `MRK-TDZ-${i ? "35" : "17"}`,
      marking_type: "touchdown_zone",
      colour: "white",
    });
  }),
  ...["TWY-A", "TWY-B", "TWY-C", "TWY-D", "TWY-E"].map((twy) => {
    const p = pavement.find((f) => f.props.tag === twy)!;
    const ring = (p.geometry as { coordinates: LonLat[][] }).coordinates[0];
    const mid = (i: number, j: number): LonLat => [(ring[i][0] + ring[j][0]) / 2, (ring[i][1] + ring[j][1]) / 2];
    return feat(
      "markings",
      { type: "LineString", coordinates: [mid(0, 1), mid(2, 3)] },
      { tag: `MRK-CL-${twy}`, marking_type: "taxiway_centreline", colour: "yellow" },
    );
  }),
  feat(
    "markings",
    line([
      [-330, 1500],
      [-330, 1470],
    ]),
    { tag: "MRK-HOLD-B1", marking_type: "holding_position", colour: "yellow" },
  ),
  feat(
    "markings",
    line([
      [-330, 0],
      [-330, -30],
    ]),
    { tag: "MRK-HOLD-C1", marking_type: "holding_position", colour: "yellow" },
  ),
  feat(
    "markings",
    line([
      [-330, -1500],
      [-330, -1530],
    ]),
    { tag: "MRK-HOLD-A2", marking_type: "holding_position", colour: "yellow" },
  ),
];

/* AGL: runway edge lights every 60 m both sides; holding-position lights */
const agl: MapFeature[] = [];
for (const [r, tag] of [
  [RWY1, "E1"],
  [RWY2, "E2"],
] as const) {
  const steps = Math.floor(r.len / 60);
  const a = ((90 - r.hdg) * Math.PI) / 180;
  const vx = -Math.sin(a),
    vy = Math.cos(a);
  for (let i = 0; i <= steps; i++) {
    const [e, n] = along(r.cx, r.cy, r.len, r.hdg, i / steps);
    for (const side of [-1, 1]) {
      const off = (r.wid / 2 + 3) * side;
      agl.push(
        feat("agl", pt(e + vx * off, n + vy * off), {
          tag: `${tag}-${String(i * 2 + (side > 0 ? 1 : 0)).padStart(3, "0")}`,
          asset_type: "agl_light",
          light_type: "runway_edge",
          circuit: tag,
          status: "in_service",
        }),
      );
    }
  }
}
for (const [tag, e, n] of [
  ["HP-B1-01", -338, 1512],
  ["HP-B1-02", -338, 1500],
  ["HP-B1-03", -338, 1488],
  ["HP-B1-04", -338, 1476],
  ["HP-C1-01", -338, 12],
  ["HP-C1-02", -338, 0],
  ["HP-A2-01", -338, -1488],
  ["HP-A2-02", -338, -1500],
] as const) {
  agl.push(
    feat("agl", pt(e, n), {
      tag,
      asset_type: "agl_light",
      light_type: "holding_position",
      status: tag === "HP-B1-03" || tag === "HP-B1-04" ? "unserviceable" : "in_service",
    }),
  );
}

/* signs, buildings, roads, perimeter */
const signs: MapFeature[] = [
  feat("signs", pt(-300, 1520), { tag: "S2397", name: "TWY B / RWY 17 mandatory sign", sign_type: "mandatory" }),
  feat("signs", pt(-300, -1480), { tag: "S2825", name: "TWY A / A2 holding sign", sign_type: "mandatory" }),
  feat("signs", pt(250, 720), { tag: "S2440", name: "Stand 40 identification", sign_type: "information" }),
];
const buildings: MapFeature[] = [
  feat("buildings", poly(rect(450, 1050, 620, 160, 80)), {
    tag: "TERMINAL",
    name: "Passenger terminal",
    use: "terminal",
  }),
  feat("buildings", poly(rect(1050, 900, 120, 90, 80)), {
    tag: "RFF",
    name: "Rescue and fire fighting station",
    use: "rff",
  }),
  feat("buildings", poly(rect(1050, -400, 160, 120, 80)), {
    tag: "MAINT",
    name: "Maintenance base",
    use: "maintenance",
  }),
  feat("buildings", poly(rect(-1300, -2600, 40, 40, 0)), { tag: "VOR", name: "VOR compound", use: "navaid" }),
  feat("buildings", poly(rect(-1200, 2500, 30, 30, 0)), {
    tag: "LLZ",
    name: "Localiser far-field monitor",
    use: "navaid",
  }),
  feat("buildings", poly(rect(1700, 200, 20, 20, 0)), { tag: "MET", name: "Anemometer mast", use: "met" }),
];
const roads: MapFeature[] = [
  feat(
    "roads",
    line([
      [1150, 1000],
      [1150, -600],
      [1700, -600],
      [1700, -1300],
    ]),
    { tag: "RD-SERVICE-E", name: "East service road" },
  ),
  feat(
    "roads",
    line([
      [-800, 2300],
      [1150, 2300],
      [1150, 1000],
    ]),
    { tag: "RD-PERIM-N", name: "Northern perimeter road" },
  ),
];
const perimeter: MapFeature[] = [
  feat("perimeter", poly([ll(-1600, 2700), ll(2000, 2700), ll(2000, -2800), ll(-1600, -2800), ll(-1600, 2700)]), {
    tag: "FENCE",
    name: "Aerodrome perimeter fence",
  }),
];

export const GEO_FEATURES: MapFeature[] = [
  ...pavement,
  ...markings,
  ...agl,
  ...signs,
  ...buildings,
  ...roads,
  ...perimeter,
];

export const GEO_LAYERS: MapLayer[] = [
  {
    key: "pavement",
    name: "Pavements",
    zIndex: 10,
    visibleDefault: true,
    style: { stroke: "#94a3b8", opacity: 0.85 },
    featureCount: pavement.length,
  },
  {
    key: "markings",
    name: "Surface Markings",
    zIndex: 20,
    visibleDefault: true,
    style: { widthPx: 1.5 },
    featureCount: markings.length,
  },
  { key: "buildings", name: "Buildings", zIndex: 30, visibleDefault: true, style: {}, featureCount: buildings.length },
  { key: "agl", name: "AGL Lighting", zIndex: 40, visibleDefault: false, style: {}, featureCount: agl.length },
  { key: "signs", name: "Signs", zIndex: 45, visibleDefault: false, style: {}, featureCount: signs.length },
  { key: "roads", name: "Service Roads", zIndex: 50, visibleDefault: true, style: {}, featureCount: roads.length },
  { key: "perimeter", name: "Perimeter", zIndex: 60, visibleDefault: true, style: {}, featureCount: perimeter.length },
];

export function featuresOf(layer: string): MapFeature[] {
  return GEO_FEATURES.filter((f) => f.layer === layer);
}
